import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import moment from "moment";
import scriptjs from "scriptjs";

import { getActivesPlans, getPlanById } from "../../../services/plan-service";
import PlanModel from "../../../models/plan.model";
import { useAuth } from "../../../contexts/auth2";
import { ACQUIRER } from "../../../models/AcquirerModel";
import OrderModel from "../../../models/OrderModel";
import { useModal } from "../../../contexts/modal";
import MCAlert from "../../../components/ModalContents/MCAlert";
import OrderService from "../../../services/orderService";
import MCPaymentInfo from "../../../components/ModalContents/MCPaymentInfo";
import { getUserProfile } from "../../../services/profileService";
import MCOrderInfo, { OrderInfoState } from "../../../components/ModalContents/MCOrderInfo";
import PreapprovalModel from "../../../models/PreapprovalModel";
import UpdatePreapprovalModel from "../../../models/UpdatePreapprovalModel";
import ResponsePreapprovalModel from "../../../models/ResponsePreapproval.model";
import MCConfirm from "../../../components/ModalContents/MCConfirm";
import { getUserToken, refreshToken, removeUser, tokenToUser, updateUserToken } from "../../../services/userService";
import { handlePromise } from "../../../utils/handlePromisse";
import { ActivateGift, getEmailAndNotUsed } from "../../../services/gift.services";

const { REACT_APP_MERCADO_PAGO_PUBLIC_KEY } = process.env;
let cardForm: any = null;

/**
 * Lógica da tela de Planos: busca de planos, brinde, agrupamento mensal/anual e
 * todo o checkout MercadoPago (preserva o comportamento original — apenas extraído
 * da página para um hook, deixando o container fino).
 */
export function usePlansPage() {
  const { user, setUser } = useAuth();
  const { setModalContent, setModalOpen } = useModal();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState<PlanModel[]>([]);
  const [dueDate, setDueDate] = useState<number>(0);
  const [planInVerification] = useState(user.unlocked === 0 && user.planId !== null);
  const [preapprovalId, setPreapprovalId] = useState<string>();
  const [isPlanCancelled] = useState(user.cancelled === 1);
  const [hasGift, setHasGift] = useState<boolean>(false);
  const [giftData, setGiftData] = useState<any>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const fetchGiftNotUsed = useCallback(async () => {
    const [result, error] = await handlePromise(getEmailAndNotUsed());
    if (error) {
      setHasGift(false);
      return;
    }
    if (result.data) {
      setGiftData(result.data);
      setHasGift(true);
    } else {
      setGiftData(null);
      setHasGift(false);
    }
  }, []);

  const activateUserGift = useCallback(async (email: string) => {
    const [result, error] = await handlePromise(ActivateGift());
    if (error) {
      setSnackbarMessage("Erro ao ativar presente.");
      setShowSnackbar(true);
      return;
    }
    if (result.data) {
      setHasGift(false);
      refreshToken(getUserToken() ?? "").then((res) => {
        removeUser();
        // @ts-ignore
        updateUserToken(res.data.newToken);
        setUser(tokenToUser());
        navigate("/dashboard/app");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPlans = useCallback(() => {
    setLoading(true);
    getActivesPlans()
      .then((result) => {
        const data = result.data;
        if (data.plans) {
          setPlans(data.plans);
        } else {
          setPlans(data);
        }
      })
      .catch((_) => {})
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const getUserPlan = useCallback(() => {
    Promise.all([getPlanById(user.planId), getActivesPlans()]).then((value) => {
      const userPlan = value[0].data as PlanModel[];
      const activesData = value[1].data;
      const availablePlans = (activesData.plans ?? activesData) as PlanModel[];

      const plansSet = new Set<string>();
      plansSet.add(JSON.stringify(userPlan));
      availablePlans.forEach((plan) => {
        plansSet.add(JSON.stringify(plan));
      });
      setPlans(Array.from(plansSet).map((value) => JSON.parse(value)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.planId]);

  const fetchProfile = () => {
    getUserProfile(user.idUser).then((value) => {
      const response = value.data;
      const lastDay = new Date(response.dat_end_plan).getTime();
      const today = new Date().getTime();
      const diffTime = Math.abs(lastDay - today);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setPreapprovalId(response.preapproval_id);
      setDueDate(diffDays);
    });
  };

  useEffect(() => {
    fetchProfile();
    if (user.planId) {
      getUserPlan();
    } else {
      fetchPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPlans, getUserPlan]);

  useEffect(() => {
    fetchGiftNotUsed();
  }, [fetchGiftNotUsed]);

  const joinPlainMercadoPago = (document: string | null, planId: number, wantsInvoiceWithCpf: boolean, couponCode?: string, payerDocument?: string) => {
    new Promise((resolve, reject) => {
      scriptjs("https://sdk.mercadopago.com/js/v2", async () => {
        try {
          const orderModel = {
            userId: user.idUser,
            planId: planId,
            acquirerId: ACQUIRER.MERCADO_PAGO.value,
            document: document ? document.replace(/\D/g, '') : null,
            payerDocument: payerDocument ? payerDocument.replace(/\D/g, '') : undefined,
            wantsInvoiceWithCpf,
            couponCode: couponCode,
          } as OrderModel;
          const result = await OrderService.create(orderModel);
          setModalContent(<MCAlert title="Pagamento" text="Você será redirecionado para a página de pagamentos" />);
          setModalOpen(true);
          setTimeout(() => {
            window.location.assign(result.data.redirect);
          }, 2000);
        } catch (e) {
          setModalContent(<MCAlert title="Erro" text="Um erro ocorreu durante o processamento das informações" />);
          setModalOpen(true);
        }
      });
    });
  };

  const handleOneTimePurchase = (plan: PlanModel) => {
    setModalContent(
      <MCOrderInfo
        plan={plan}
        onAssign={(values: OrderInfoState, couponCode) => {
          const paymentCpf = values.paymentCpf.replace(/\D/g, '')
          const wantsInvoiceWithCpf = values.invoiceWithCpf === 'yes'
          const invoiceCpf = wantsInvoiceWithCpf
            ? values.invoiceCpfOption === 'other' ? values.invoiceCpf.replace(/\D/g, '') : paymentCpf
            : null
          joinPlainMercadoPago(invoiceCpf, plan.id as number, wantsInvoiceWithCpf, couponCode, paymentCpf)
        }}
      />
    );
    setModalOpen(true);
  };

  const openCardForm = (plan: PlanModel, codPayment?: string) => {
    new Promise((resolve, reject) => {
      scriptjs("https://sdk.mercadopago.com/js/v2", async () => {
        const callback = () =>
          setTimeout(() => {
            if (cardForm != null) {
              cardForm.unmount();
            }
            // @ts-ignore
            const mercadopago = new MercadoPago(REACT_APP_MERCADO_PAGO_PUBLIC_KEY);

            cardForm = mercadopago.cardForm({
              amount: plan.value.toString(),
              iframe: true,
              form: {
                id: "form-checkout",
                cardNumber: { id: "form-checkout__cardNumber", placeholder: "* Número do cartão" },
                expirationDate: { id: "form-checkout__expirationDate", placeholder: "* Validade (mm/AA)" },
                securityCode: { id: "form-checkout__securityCode", placeholder: "* Código de segurança" },
                cardholderName: { id: "form-checkout__cardholderName", placeholder: "* Titular do cartão" },
                issuer: { id: "form-checkout__issuer", placeholder: "Bandeira" },
                installments: { id: "form-checkout__installments", placeholder: "Parcelas" },
                identificationType: { id: "form-checkout__identificationType", placeholder: "Tipo de documento" },
                identificationNumber: { id: "form-checkout__identificationNumber", placeholder: "* Número do documento" },
                cardholderEmail: { id: "form-checkout__cardholderEmail", placeholder: "E-mail" },
              },
              callbacks: {
                onError: (error: any) => {
                  console.log(error);
                },
                onFormMounted: (error: any) => {
                  if (error) {
                    messageError();
                    return;
                  }
                  setTimeout(() => {
                    document.querySelectorAll("[id=form-checkout], .warning-fields").forEach((x: any) => (x.style.display = ""));
                    // @ts-ignore
                    document.querySelector("[id=circularProgress]").style.display = "none";
                  }, 1500);
                },
                onSubmit: async (event: { preventDefault: () => void }) => {
                  await submitPlanPreapproval(event, plan, codPayment);
                },
                onFetching: (resource: any) => {
                  console.log("Fetching resource: ", resource);
                  return () => {};
                },
              },
            });
          }, 1000);
        setModalContent(<MCPaymentInfo callback={callback} />);
        setModalOpen(true);
      });
    });
  };

  const handleChangeCard = (plan: PlanModel) => {
    if (preapprovalId) {
      openCardForm(plan, preapprovalId);
    }
  };

  const handlePlanSelected = async (plan: PlanModel) => {
    if (plan.preapproval_enabled) {
      try {
        const preapproval = {
          userId: user.idUser,
          planId: plan.id,
          acquirerId: ACQUIRER.MERCADO_PAGO.value,
          cardToken: "",
          payerEmail: user.email,
        } as PreapprovalModel;
        await createPlanPreapproval(preapproval);
      } catch (e) {
        messageError();
      }
      return;
    }
    handleOneTimePurchase(plan);
  };

  const submitPlanPreapproval = async (event: { preventDefault: () => void }, plan: PlanModel, codPayment?: string) => {
    event.preventDefault();
    try {
      const { token, cardholderEmail } = cardForm.getCardFormData();
      const updatePreapproval = { userId: user.idUser, cardToken: token } as UpdatePreapprovalModel;

      if (codPayment != null && codPayment !== "") {
        await updatePlanPreapproval(codPayment, updatePreapproval);
        return;
      }

      const preapproval = {
        userId: user.idUser,
        planId: plan.id,
        acquirerId: ACQUIRER.MERCADO_PAGO.value,
        cardToken: token,
        payerEmail: cardholderEmail,
      } as PreapprovalModel;
      await createPlanPreapproval(preapproval);
    } catch (e) {
      let message: string | undefined = undefined;
      // @ts-ignore
      if (e.response != null && e.response.status >= 400 && e.response.status <= 499) {
        // @ts-ignore
        message = e.response.data.message;
      }
      messageError(message);
    }
  };

  const updatePlanPreapproval = async (codPayment: string, updatePreapproval: UpdatePreapprovalModel) => {
    setModalOpen(false);
    setLoading(true);
    await OrderService.updatePreapproval(codPayment, updatePreapproval);
    setLoading(false);
    setModalContent(<MCAlert title="Pagamento" text="Os dados de pagamento foram alterados com sucesso!" />);
    setModalOpen(true);
  };

  const createPlanPreapproval = async (preapproval: PreapprovalModel) => {
    setModalOpen(false);
    setLoading(true);
    const response = await OrderService.createPreapproval(preapproval);
    const { initPoint, paymentId, status, externalReference } = response.data as ResponsePreapprovalModel;

    if (initPoint) {
      window.location.assign(initPoint);
    } else {
      window.location.assign(
        `/notification/mercadopago?payment_id=${paymentId}&status=${status}&external_reference=${externalReference}&is_preapproval=true`
      );
    }
  };

  const messageError = (message?: string) => {
    setModalOpen(false);
    setLoading(false);
    setModalContent(<MCAlert title="Erro" text={message == null ? "Aconteceu um erro inesperado tente novamente" : message} />);
    setModalOpen(true);
  };

  const cancelPlanPreapproval = async (codPayment: string) => {
    setModalContent(
      <MCConfirm
        title="Cancelar Plano"
        text="<p>Deseja realmente cancelar sua assinatura? </p> <p><b>Essa ação é irreversível! </b> </p>"
        confirmButtonText="CONFIRMAR"
        cancelButtonText="CANCELAR"
        onConfirm={async () => {
          setModalOpen(false);
          setLoading(true);
          await OrderService.cancelPreapproval(codPayment);
          await messageSuccessPlanPreapprovalCancelled();
        }}
        onCancel={() => setModalOpen(false)}
      />
    );
    setModalOpen(true);
  };

  const messageSuccessPlanPreapprovalCancelled = async () => {
    const response = await getUserProfile(user.idUser);
    const result = response.data;
    let message = "Seu plano foi cancelado com sucesso!";

    if (result.dat_end_plan != null && result.dat_end_plan.toString().trim() !== "") {
      message = `Seu plano foi cancelado com sucesso! Você pode continuar com seus benefícios até ${moment(result.dat_end_plan).format("DD/MM/YYYY")}`;
    }

    setModalOpen(false);
    setModalContent(
      <MCAlert
        title="Plano Cancelado"
        text={message}
        onClose={() => {
          refreshToken(getUserToken() ?? "").then((res) => {
            removeUser();
            // @ts-ignore
            updateUserToken(res.data.newToken);
            setUser(tokenToUser());
            setModalOpen(false);
            navigate("/dashboard/app");
          });
        }}
      />
    );
    setModalOpen(true);
  };

  // Planos são por duração (Mensal/Trimestral/Semestral/Anual) — cada um é um plano
  // distinto; a UI mostra o preço/mês equivalente + o total cobrado no período.
  const displayedPlans = plans;

  return {
    user,
    loading,
    displayedPlans,
    dueDate,
    planInVerification,
    preapprovalId,
    isPlanCancelled,
    hasGift,
    giftData,
    activateUserGift,
    showSnackbar,
    snackbarMessage,
    setShowSnackbar,
    handlePlanSelected,
    handleOneTimePurchase,
    handleChangeCard,
    cancelPlanPreapproval,
  };
}
